<template>
  <div class="info-content">
    <div class="loading" v-if="loading">
      <pn-spinner color="blue" size="medium"></pn-spinner>
    </div>
    <div v-if="!loading && order">
      <div class="mobile-header" v-if="isMobileView">
        <h2>{{ $t(titleTranslationString) }} # {{ cartId }}</h2>

        <pn-icon
          @click="downloadReceipt()"
          symbol="download"
          color="blue700"
          size="medium"
          solid
        ></pn-icon>
      </div>

      <div
        class="status-header"
        v-if="
          !order.product.ownMaterial ||
            (order.product.ownMaterial && order.status !== 'UNPROCESSED')
        "
      >
        <div
          class="status-bar"
          :class="['status-bar', statuses[order.status].background]"
        >
          <pn-icon
            :symbol="statuses[order.status].symbol"
            :color="statuses[order.status].color"
            size="medium"
            solid
          >
          </pn-icon>
          <span class="small white-text">{{
            $t(statuses[order.status].text)
          }}</span>
        </div>
        <div
          class="disclaimer"
          :class="['disclaimer', statuses[order.status].disclaimer]"
        >
          <p class="tiny">
            {{ $t(statuses[order.status].message) }}
          </p>
        </div>
      </div>
      <div class="material-preview" v-if="!isOwnmaterial">
        <PreviewMaterialComponent
          :src="order.materialUrl"
          :controls="['pagination']"
        ></PreviewMaterialComponent>
      </div>
      <div class="pricing-info" v-if="!isOwnmaterial">
        <div class="details">
          <div
            class="name title"
            v-html="$t('W2P_TRANSACTION_TOTAL_PAID_INCL_VAT')"
          ></div>
          <div class="value title">
            {{ $n(totalWithVAT, 'currency', 'sv-SE') }}
          </div>
        </div>
        <hr />
        <div class="details">
          <div class="name">
            {{ $t('W2P_TRANSACTION_BASE_PRICE') }}
            <p class="tiny">
              {{ $n(basePricePerPiece, 'currency', 'sv-SE') }}/{{
                $t('W2P_PRODUCT_DEFINITION_PIECE')
              }}
              x {{ $n(recipients) }} {{ $t('W2P_RECIPIENTS') }}
            </p>
          </div>
          <div class="value">
            {{ $n(basePrice, 'currency', 'sv-SE') }}
          </div>
        </div>
        <div class="details" v-for="extra in extras" :key="extra.name">
          <div class="name">
            {{ $t(extra.name) }}
            <p class="tiny">
              {{ $n(extra.pricePerPiece, 'currency', 'sv-SE') }}/{{
                $t('W2P_PRODUCT_DEFINITION_PIECE')
              }}
              x {{ $n(recipients) }} {{ $t('W2P_RECIPIENTS') }}
            </p>
          </div>
          <div class="value">
            {{ $n(extra.price, 'currency', 'sv-SE') }}
          </div>
        </div>
        <div class="details" v-if="postage">
          <div class="name">
            {{ $t('W2P_TRANSACTION_POSTAGE') }}
            <p class="tiny">
              {{ $n(postagePricePerPiece, 'currency', 'sv-SE') }}/{{
                $t('W2P_PRODUCT_DEFINITION_PIECE')
              }}
              x {{ $n(recipients) }} {{ $t('W2P_RECIPIENTS') }}
            </p>
          </div>
          <div class="value">
            {{ $n(postage, 'currency', 'sv-SE') }}
          </div>
        </div>
        <div class="details">
          <div class="name">
            {{ $t('W2P_TRANSACTION_TOTAL_EXCL_VAT') }}
          </div>
          <div class="value">
            {{ $n(totalPrice, 'currency', 'sv-SE') }}
          </div>
        </div>
      </div>
      <hr v-if="!isOwnmaterial" />
      <div class="order-summary">
        <pn-table class="custom-responsive" :disabled="true" striped>
          <table>
            <tbody>
              <tr>
                <td>
                  <div class="small bold grey">{{ $t('W2P_ORDER_NAME') }}</div>
                  <div class="small details">{{ order.name }}</div>
                </td>
              </tr>
              <tr v-if="!isOwnmaterial">
                <td>
                  <div class="small bold grey">{{ $t('SIZE') }}</div>
                  <div class="small details">
                    {{ orderFormat }}
                  </div>
                </td>
              </tr>
              <tr v-if="!isOwnmaterial">
                <td>
                  <div class="small bold grey">{{ $t('W2P_PRINTING') }}</div>
                  <div class="small details">
                    {{ materialPages }} {{ $t('W2P_PAGES') }}
                    {{ materialPrintType }} {{ $t('W2P_FULL_COLOR') }}
                  </div>
                </td>
              </tr>
              <tr v-if="!isOwnmaterial">
                <td>
                  <div class="small bold grey">{{ $t('W2P_PAPER') }}</div>
                  <div class="small details">
                    {{ paperInfo }}
                  </div>
                </td>
              </tr>
              <tr v-if="!isOwnmaterial">
                <td>
                  <div class="small bold grey">{{ $t('W2P_ENVELOPE') }}</div>
                  <div class="small details">
                    {{ envelope }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div class="small bold grey">
                    {{ $t('EDI_CREATE_PAGE_SENDER') }}
                  </div>
                  <div class="small details">
                    <p>{{ sender.name }}</p>
                    <p>{{ sender.address }}</p>
                    <p>{{ sender.addressOptional }}</p>
                    <p>{{ sender.postalCode }}</p>
                    <p>{{ sender.city }}</p>
                    <p>{{ sender.country }}</p>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div class="small bold grey">{{ $t('W2P_COMPANY') }}</div>
                  <div class="small details">
                    {{ company }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div class="small bold grey">{{ $t('W2P_RECIPIENTS') }}</div>
                  <div class="small details">
                    {{ recipients }}
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div class="small bold grey">{{ $t('W2P_DELIVERY') }}</div>
                  <div class="small details">
                    <p
                      v-html="
                        $t('W2P_DELIVERY_APPROVE_DETAILS', {
                          formattedDate: format(order.distributionDate, lang)
                        })
                      "
                    ></p>
                    <p
                      v-html="
                        $t('W2P_LETTER_OPTIONS_COST_ESTIMATE_NOTE_TOOLTIP')
                      "
                    ></p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </pn-table>
      </div>
    </div>
    <div class="button-wrapper" v-if="isOwnmaterial && !loading">
      <pn-button-container size="medium">
        <button @click="downloadPdf('slip')">
          {{ $t('W2P_DOWNLOAD_SLIP') }}
          <pn-icon symbol="download" color="white" size="small" solid></pn-icon>
        </button>
      </pn-button-container>
      <pn-button-container size="medium">
        <button @click="downloadPdf('list')">
          {{ $t('W2P_DOWNLOAD_PACKINGLIST') }}
          <pn-icon symbol="download" color="white" size="small" solid></pn-icon>
        </button>
      </pn-button-container>
    </div>

    <pn-button-container
      class="cancel-order"
      v-if="showCancelOrder && !loading"
      size="medium"
      secondary
      danger
    >
      <button @click="confirmCancelOrder">{{ $t('W2P_CANCEL_ORDER') }}</button>
    </pn-button-container>

    <pn-modal id="cancel-order-modal" click-outside-to-close>
      <h2>{{ $t('W2P_CANCEL_ORDER') }}: # {{ cartId }}</h2>
      <div class="confirmation-body">
        <p v-if="!cancelOrderError && !cancelOrderSuccess">
          {{ $t('W2P_CANCEL_ORDER_CONFIRMATION') }}
        </p>
        <pn-toast v-if="cancelOrderSuccess" icon="check" appearance="success">
          <p>{{ $t('W2P_CANCEL_ORDER_SUCCESS') }}</p>
        </pn-toast>

        <pn-toast
          v-if="cancelOrderError"
          icon="alert-exclamation-circle"
          appearance="warning"
        >
          <p>{{ $t('W2P_CANCEL_ORDER_FAILED') }}</p>
        </pn-toast>

        <div class="actions">
          <pn-button-container size="medium" secondary>
            <button :disabled="cancelingOrder" @click="cancelConfirmation">
              {{ cancelOrderSuccess ? $t('CLOSE') : $t('CANCEL') }}
            </button>
          </pn-button-container>
          <pn-button-container
            size="medium"
            :loading="cancelingOrder"
            :success="cancelOrderSuccess"
          >
            <button :disabled="cancelingOrder" @click="cancelOrder">
              {{ cancelOrderSuccess ? $t('SUCCESS') : $t('YES') }}
              <pn-icon
                v-if="cancelOrderSuccess"
                symbol="check"
                color="white"
                size="small"
              ></pn-icon>
            </button>
          </pn-button-container>
        </div>
      </div>
    </pn-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import _ from 'lodash'
import axios from 'axios'
import * as helper from '@/helpers/utilHelper'

import orderDetails from '@/components/order-details-modal/orderDetails.json'
import PreviewMaterialComponent from '@/components/PreviewMaterialComponent.vue'

export default {
  components: {
    PreviewMaterialComponent
  },
  name: 'OrderInfoComponent',
  props: {
    order: {
      type: Object
    },
    loading: {
      type: Boolean,
      default: true
    },
    isMobileView: {
      type: Boolean
    }
  },
  data() {
    return {
      statuses: orderDetails.statuses,
      cancelingOrder: false,
      cancelOrderError: false,
      cancelOrderSuccess: false
    }
  },
  computed: {
    ...mapState(['lang', 'accessToken']),
    formatUnit() {
      return this.order.product.format.units[this.lang]
    },
    orderFormat() {
      const key = this.order.product.format.description_language
        ? 'description_language'
        : 'description'
      return this.order.product.format[key][this.lang]
    },
    materialPages() {
      return this.order.numberOfPages
    },
    materialPrintType() {
      return this.order.product.pages_information[this.lang]
    },
    paperInfo() {
      return this.order.product.information[this.lang]
    },
    envelope() {
      return this.order.product.envelope[this.lang]
    },
    sender() {
      return {
        name: this.order.senderInfo.addressLineOne,
        address: this.order.senderInfo.street,
        addressOptional: '',
        postalCode: this.order.senderInfo.zipcode,
        city: this.order.senderInfo.city,
        country: this.order.senderInfo.country
      }
    },
    company() {
      const company = this.order.contactInfo.company
      return company ? company.name : ''
    },
    recipients() {
      return this.order.numberOfRecipients
    },
    totalWithVAT() {
      return this.order.pricing.totalWithVAT / 100
    },
    totalPrice() {
      return this.order.pricing.total / 100
    },
    basePrice() {
      return this.order.pricing.baseProduct.price / 100
    },
    basePricePerPiece() {
      return (
        this.order.pricing.baseProduct.price /
        this.order.pricing.baseProduct.count /
        100
      )
    },
    extras() {
      if (!this.order.pricing.additionalServices.length) {
        return []
      }

      return this.order.pricing.additionalServices.map(service => {
        let extra = {}
        extra.name = service.name[this.lang]
        extra.price = service.price / 100
        extra.count = service.count
        extra.pricePerPiece = service.price / 100 / service.count
        extra.priceWithVat = service.priceWithVat / 100
        return extra
      })
    },
    postage() {
      return _.sumBy(this.order.pricing.postage, postage => {
        return postage.price / 100
      })
    },
    postagePricePerPiece() {
      return this.postage / this.order.pricing.baseProduct.count
    },
    isOwnmaterial() {
      return this.order ? this.order.product.ownMaterial : false
    },
    cartId() {
      return this.order
        ? this.order.cartId || this.order.ODRBookingInformation.bookingId
        : ''
    },
    titleTranslationString() {
      return helper.getCatogeryTranslationString(
        this.order.product.siteCategory
      )
    },
    showCancelOrder() {
      return this.order ? this.order.status === 'UNPROCESSED' : false
    }
  },
  methods: {
    format(date, language) {
      moment.locale(language)
      return moment(date).format('LL')
    },
    downloadPdf(typeOfPdf) {
      let url = ''
      let fileName = ''

      if (typeOfPdf === 'slip') {
        url = this.order.odrUrls['odrSlips.pdf']
        fileName = this.order.odrDocuments[1]
      } else if (typeOfPdf === 'list') {
        url = this.order.odrUrls['odrSummary.pdf']
        fileName = this.order.odrDocuments[0]
      }

      axios({
        method: 'get',
        url: url,
        responseType: 'arraybuffer'
      }).then(response => {
        this.forceFileDownload(response, fileName)
      })
    },
    forceFileDownload(response, fileName) {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName) //or any other extension
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    downloadReceipt() {
      this.$emit('downloadReceipt')
    },
    confirmCancelOrder() {
      if (!this.showCancelOrder || this.cancelOrderSuccess) {
        return
      }
      document.getElementById('cancel-order-modal').open()
    },
    cancelConfirmation() {
      document.getElementById('cancel-order-modal').close()
      document.body.style.overflow = 'hidden'
      this.cancelingOrder = this.cancelOrderError = this.cancelOrderSuccess = false
    },
    cancelOrder() {
      this.cancelingOrder = true

      const orderId = this.order.orderId
      axios({
        method: 'post',
        url:
          process.env.VUE_APP_W2P_API_BASE_URL +
          '/orders/cancelOrder?orderId=' +
          orderId,
        data: {
          orderId
        },
        headers: { authorization: this.accessToken }
      })
        .then(() => {
          this.cancelOrderSuccess = true
          this.cancelingOrder = false
          this.$emit('orderCancelled')
        })
        .catch(() => {
          this.cancelOrderError = true
          this.cancelingOrder = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.info-content {
  width: 100%;

  hr {
    border-color: $gray200;
  }

  .loading {
    text-align: center;
    padding-top: 2.5em;
  }

  .grey {
    color: $black;
  }

  .mobile-header {
    margin-bottom: 2em;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .status-header {
    width: 100%;

    .warning {
      background-color: $warning;
    }

    .green700 {
      background-color: $green700;
    }

    .blue700 {
      background-color: $blue700;
    }

    .gray700 {
      background-color: $gray700;
    }

    .coral50 {
      background-color: $coral50;
    }

    .green50 {
      background-color: $green50;
    }

    .gray50 {
      background-color: $gray50;
    }

    .blue50 {
      background-color: $blue50;
    }

    .status-bar {
      color: white;
      padding: 1em;
    }

    span.small {
      font-size: 0.9em;
    }

    .white-text {
      color: $white !important;
      margin-left: 1em;
      vertical-align: sub;
    }

    .disclaimer {
      padding: 0.5em;
      height: auto;
    }
  }

  .material-preview {
    margin: 0 auto;
    padding: 2em 3em;
    width: 80%;
  }

  .pricing-info {
    margin: 2em 0;

    .details {
      display: flex;
      margin-top: 0.58em;
      justify-content: space-between;
      color: $black;
      font-size: 0.875em;
      font-weight: 300;
      line-height: 1.5em;

      .name {
        &.title {
          font-size: 1.143em;
          font-weight: 500;
        }

        .tiny {
          color: $gray700;
        }
      }

      .value.title {
        font-size: 1.75em;
        font-weight: 800;
      }
    }

    hr {
      margin-top: 1.5em;
      color: $gray200;
    }
  }

  .order-summary {
    margin-top: 2em;

    pn-table {
      margin-bottom: 1.5em;
    }
    .details {
      color: $gray900;
    }
  }

  .button-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1em;

    pn-button-container {
      width: 100%;

      &:first-child {
        margin-right: 0.5em;
      }

      &:last-child {
        margin-left: 0.5em;
      }
    }
  }

  pn-button-container.cancel-order {
    width: 100%;
    margin-bottom: 1em;
  }

  #cancel-order-modal {
    .confirmation-body {
      margin-top: 2em;
    }

    .actions {
      margin-top: 2em;
      text-align: right;
      display: flex;

      pn-button-container {
        width: 100%;
      }

      pn-button-container:first-child {
        padding-right: 0.5em;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .info-content {
    .material-preview {
      margin: 0 auto;
      padding: 2px;
      width: 100%;
    }
  }

  .button-wrapper {
    display: flex;
    flex-direction: column;

    pn-button-container {
      margin: 0 !important;

      &:first-child {
        margin-bottom: 1em !important;
      }
    }
  }
}
</style>
